import { sendEvent } from '$store/sdk/analytics.ts'
import { useCart } from 'apps/vtex/hooks/useCart.ts'
import type { OrderForm } from 'apps/vtex/utils/types.ts'
import type { TrinioTrackingArgs } from './types.ts'

type AnalyticsExtras = {
    shipping_tier?: string
    payment_type?: string
}

type PaymentSystemLike = {
    id: number | string
    name?: string
    groupName?: string
}

/** Labels de negócio pedidas pela Métricas Boss (item 8). */
const PAYMENT_TYPE_LABEL = {
    pix: 'Pix',
    creditCard: 'Cartão de Crédito',
    debitCard: 'Cartão de Débito',
    cartaoTorra: 'Cartão Torra',
} as const

const resolveOrderForm = (orderForm?: Readonly<OrderForm> | null) => {
    const { cart } = useCart()
    return orderForm ?? cart.value
}

const getSelectedShippingTier = (orderForm?: Readonly<OrderForm> | null) =>
    orderForm?.shippingData?.logisticsInfo?.find(({ selectedSla }) => selectedSla)
        ?.selectedSla

const normalize = (value?: string) =>
    value
        ?.normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim() ?? ''
const toBusinessPaymentType = (paymentSystem?: PaymentSystemLike | null) => {
    if (!paymentSystem) return undefined

    const group = normalize(paymentSystem.groupName)
    const name = normalize(paymentSystem.name)

    if (name.includes('torra')) return PAYMENT_TYPE_LABEL.cartaoTorra

    if (
        group === 'instantpayment' ||
        group === 'promissory' ||
        name.includes('pix')
    ) {
        return PAYMENT_TYPE_LABEL.pix
    }

    if (group === 'debitcard' || name.includes('debito')) {
        return PAYMENT_TYPE_LABEL.debitCard
    }

    if (
        group === 'creditcard' ||
        name.includes('credito') ||
        name.includes('visa') ||
        name.includes('master') ||
        name.includes('elo') ||
        name.includes('amex') ||
        name.includes('hipercard') ||
        name.includes('diners')
    ) {
        return PAYMENT_TYPE_LABEL.creditCard
    }

    // Fallback legível: name limpo da VTEX (nunca ID numérico)
    return paymentSystem.name?.trim() || undefined
}

const getPaymentType = (orderForm?: Readonly<OrderForm> | null) => {
    const payments = orderForm?.paymentData?.payments as
        | { paymentSystem?: string }[]
        | undefined
    const paymentSystemId = payments?.[0]?.paymentSystem
    if (!paymentSystemId) return undefined

    const paymentSystem = orderForm?.paymentData?.paymentSystems?.find(
        ({ id }) => String(id) === String(paymentSystemId),
    ) as PaymentSystemLike | undefined

    return toBusinessPaymentType(paymentSystem)
}

/**
 * Monta o payload GA4 recomendado a partir do orderForm
 * passado pelos callbacks do Trinio Checkout SDK.
 * @see https://docs.trinio.app/trinio-checkout-sdk
 */
const getAnalyticsPayload = (
    orderForm?: Readonly<OrderForm> | null,
    extras?: AnalyticsExtras,
) => {
    const { mapItemsToAnalyticsItems } = useCart()
    const of = resolveOrderForm(orderForm)

    if (!of?.items?.length) return null

    // orderForm.value já inclui descontos (ex.: Pix) — não somar Discounts de volta
    return {
        currency: of.storePreferencesData?.currencyCode ?? 'BRL',
        value: Number(((of.value ?? 0) / 100).toFixed(2)),
        coupon: of.marketingData?.coupon ?? undefined,
        items: mapItemsToAnalyticsItems(of),
        ...extras,
    }
}

/** Disparada ao abrir o checkout (`trackBeginCheckout`). */
export const trackBeginCheckout = (
    { orderForm }: TrinioTrackingArgs,
): Promise<void> => {
    const params = getAnalyticsPayload(orderForm)
    if (!params) return Promise.resolve()

    sendEvent({ name: 'begin_checkout', params })
    return Promise.resolve()
}

/** Disparada ao selecionar um método de entrega (`trackAddShippingInfo`). */
export const trackAddShippingInfo = (
    { orderForm }: TrinioTrackingArgs,
): Promise<void> => {
    const of = resolveOrderForm(orderForm)
    const shippingTier = getSelectedShippingTier(of)

    if (!shippingTier) return Promise.resolve()

    const params = getAnalyticsPayload(of, { shipping_tier: shippingTier })
    if (!params) return Promise.resolve()

    sendEvent({ name: 'add_shipping_info', params })
    return Promise.resolve()
}

/** Disparada ao submeter o pagamento (`trackAddPaymentInfo`). */
export const trackAddPaymentInfo = (
    { orderForm }: TrinioTrackingArgs,
): Promise<void> => {
    const of = resolveOrderForm(orderForm)
    const paymentType = getPaymentType(of)

    const params = getAnalyticsPayload(
        of,
        paymentType ? { payment_type: paymentType } : undefined,
    )

    console.log('[Trinio] trackAddPaymentInfo', {
        payment_type: paymentType,
        hasOrderForm: Boolean(of),
        params,
    })

    if (!params) return Promise.resolve()

    sendEvent({ name: 'add_payment_info', params })
    return Promise.resolve()
}
